import { createAsyncThunk, createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import { api } from "../../api";
import ToastAction from "../../components/ToastAction";
import { ACTIVE_WEBSITE_KEY } from "../../constants";

export const fetchWebsites = createAsyncThunk('websites/fetchWebsites', async ({ company_id }) => {
  return await api.sites().get(company_id)
})

export const createWebsite = createAsyncThunk('websites/createWebsite', async ({ company_id, site }) => {
  const body = { site_url: "", title: "", company_id }
  const { id } = await api.sites().create(body)
  await api.sites().update({ ...site, website_id: id })
  return { ...site, id }
})

export const updateWebsite = createAsyncThunk('websites/updateWebsite', async ({ site }) => {
  await api.sites().update(site)
  return site
})

export const deleteWebsite = createAsyncThunk('websites/deleteWebsite', async ({ company_id, id }) => {
  await api.sites().delete(company_id, id)
  return id
})


const websitesAdapter = createEntityAdapter({
  selectId: (website) => website.id,
})

const websitesSlice = createSlice({
  name: 'websites',
  initialState: websitesAdapter.getInitialState({ showMessage: false, loading: false, error: { status: false, message: '' }, activeWebsite: JSON.parse(localStorage.getItem(ACTIVE_WEBSITE_KEY)), idLoading: null }),
  reducers: {
    selectedWebsite: (state, { payload }) => {
      state.activeWebsite = payload
      localStorage.setItem(ACTIVE_WEBSITE_KEY, JSON.stringify(payload))
    },
    closeMessageWebsite: (state, _action) => {
      state.showMessage = false
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchWebsites.pending, state => {
      state.loading = true
    })
      .addCase(fetchWebsites.fulfilled, (state, { payload }) => {
        state.loading = false
        state.error = { status: false, message: null }
        if (payload.length <= 0) {
          state.activeWebsite = null
          localStorage.setItem(ACTIVE_WEBSITE_KEY, null)
        }

        if (payload.length > 0) {
          if (!payload.some(payl => payl.id === state.activeWebsite?.id)) state.activeWebsite = payload[0]
          const currentWebsiteSelected = payload.filter(payl => payl.id === state.activeWebsite?.id)?.[0]
          if (currentWebsiteSelected) state.activeWebsite = currentWebsiteSelected
        }

        websitesAdapter.setAll(state, payload)
      })
      .addCase(fetchWebsites.rejected, (state, { error }) => {
        state.loading = false
        state.error = { status: true, message: error.message }
      })
      .addCase(deleteWebsite.pending, state => {
        state.loading = true
        state.idLoading = toast.loading('Suppression en cours...')
      })
      .addCase(deleteWebsite.fulfilled, (state, { payload: id }) => {
        state.loading = false
        state.error = { status: false, message: null }
        if (state.activeWebsite?.id === id) {
          state.activeWebsite = null
          localStorage.setItem(ACTIVE_WEBSITE_KEY, null)
        }
        websitesAdapter.removeOne(state, id)
        toast.custom(t =>
          <ToastAction
            toastRef={t}
            label='Yeah !!'
            description="Votre site a bien été supprimé."
            saraExpression="wink"
          />
        )
        toast.dismiss(state.idLoading)
      })
      .addCase(deleteWebsite.rejected, (state, { error }) => {
        state.loading = false
        state.error = { status: true, message: error.message }
        toast.custom(t =>
          <ToastAction
            toastRef={t}
            label='Oups !!'
            description="Nous avons rencontré un problème lors de la suppression de votre site. Veuillez réessayer SVP !"
            saraExpression="sorry"
          />
        )
        toast.dismiss(state.idLoading)
      })
      .addCase(createWebsite.pending, state => {
        state.loading = true
        state.idLoading = toast.loading('Ajout en cours...')
      })
      .addCase(createWebsite.fulfilled, (state, { payload }) => {
        state.loading = false
        state.error = { status: false, message: null }
        state.showMessage = true
        websitesAdapter.addOne(state, payload)
        toast.dismiss(state.idLoading)
      })
      .addCase(createWebsite.rejected, (state, { error }) => {
        state.loading = false
        state.error = { status: true, message: error.message }
        toast.custom(t =>
          <ToastAction
            toastRef={t}
            label='Oups !!'
            description="Nous avons rencontré un problème lors de la création de votre site. Veuillez réessayer SVP !"
            saraExpression="sorry"
          />
        )
        toast.dismiss(state.idLoading)
      })
      .addCase(updateWebsite.pending, state => {
        state.loading = true
        state.idLoading = toast.loading('Enregistrement...')
      })
      .addCase(updateWebsite.fulfilled, (state, { payload }) => {
        state.loading = false
        state.error = { status: false, message: null }
        if (state.activeWebsite?.id === payload?.website_id) {
          state.activeWebsite = payload
          localStorage.setItem(ACTIVE_WEBSITE_KEY, JSON.stringify(payload))
        }
        websitesAdapter.updateOne(state, payload)

        toast.custom(t =>
          <ToastAction
            toastRef={t}
            label='Yeah !!'
            description="Votre site a bien été mis à jour."
            saraExpression="wink"
          />
        )
        toast.dismiss(state.idLoading)
        state.idLoading = null
      })
      .addCase(updateWebsite.rejected, (state, { error }) => {
        state.loading = false
        state.error = { status: true, message: error.message }
        toast.custom(t =>
          <ToastAction
            toastRef={t}
            label='Oups !!'
            description="Nous avons rencontré un problème lors de la mise à jour de ce site. Veuillez réessayer SVP !"
            saraExpression="sorry"
          />
        )
        toast.dismiss(state.idLoading)
        state.idLoading = null
      })
  }
})

const WebsitesSelectors = websitesAdapter.getSelectors(state => state.websites)

export const {
  selectIds,
  selectEntities,
  selectById,
  selectTotal,
  selectAll,
  loading,
  activeWebsite
} = WebsitesSelectors

export const { selectedWebsite, closeMessageWebsite, switchOnClose } = websitesSlice.actions

export default websitesSlice.reducer